import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Box,
  VStack,
  HStack,
} from '@chakra-ui/react';

import Icon from 'components/Icon';
import Text from 'components/Text';

interface FAQItem {
  question: string;
  answer: string;
  legacyOnly?: boolean;
  legacyAnswer?: string;
}

const QUESTIONS_AND_ANSWERS: FAQItem[] = [
  {
    question: `What's the difference between Monthly, Yearly, and Lifetime?`,
    answer: `All paid plans include full access to projects, challenges, practice problems, Figma designs, the built-in code editor, Merlin AI, prototypes, code frames, and private code. Monthly ($8/mo) comes with 5 BigTokens/mo and up to 10 Code Frames. Yearly ($64/yr, ~$5.33/mo) includes 10 BigTokens/mo, up to 100 Code Frames, and access to the Yearly Daredevils Discord. Lifetime ($299 one-time) gives you 15 BigTokens/mo, up to 100 Code Frames, priority support, all future updates, and access to the Lifetime Legends Discord. Both Monthly and Yearly start with a 7-day free trial.`,
  },
  {
    question: 'Is being an experienced Developer required to use your app?',
    answer: `No, BigDevSoon is designed for all skill levels, from beginners to experienced developers. Merlin AI helps you generate code, get reviews, and learn as you build — so you can start learning and growing regardless of your current expertise.`,
  },
  {
    question: 'What is Merlin AI on BigDevSoon?',
    answer:
      'Merlin AI is your AI-powered vibe coding companion on BigDevSoon. It helps you generate code solutions, review your code, get design feedback, and prototype ideas — all through a conversational chat interface. Use slash commands like /solution, /review, /feedback, and /roast to get targeted help while working on projects and challenges.',
  },
  {
    question: `What benefits do I get from the dedicated Discord channels for Yearly and Lifetime subscribers?`,
    answer: `Our dedicated Discord channels for Yearly and Lifetime subscribers is a close-knit community for our most committed learners. By being a part of this exclusive group, you receive priority support, valuable tips & tricks, and insights to boost your coding journey. It's our way of providing extra attention and resources to those who support us the most, ensuring they succeed in their learning goals.`,
  },
  {
    legacyOnly: true,
    question: 'Why should I upgrade to a paid plan?',
    answer:
      'While your legacy free access gives you basic functionality, upgrading unlocks the full BigDevSoon experience. Paid plans include Merlin AI for code generation, reviews, and feedback, professional Figma designs for all projects, increased BigToken allocations for extended Merlin AI usage and file storage, private code storage, up to 20K characters per tab, and access to prototypes and code frames. Each plan tier increases your monthly BigToken allocation, helping you get more out of Merlin AI and expand your file storage.',
  },
  {
    question: `Does my subscription automatically renew?`,
    answer: `Yes, Monthly and Yearly subscriptions auto-renew at the end of their term. Lifetime is a one-time payment with no renewal needed.`,
  },
  {
    question: `What happens when my subscription expires?`,
    legacyAnswer: `As a legacy user, if your subscription expires you'll retain basic access to the app. Your BigToken count resets to 3/month, premium features like Merlin AI chat and private code are revoked, and code over 10,000 characters can't be saved. If you had more than 10 Code Frames, you'll need to remove some before creating new ones. Merlin AI chat history stays visible but you won't be able to send new messages. Yearly subscribers will also be removed from the dedicated Discord channel.`,
    answer: `If your trial or subscription expires, your access will be limited to your profile, billing, and settings pages only. To regain full access to projects, challenges, practice problems, the code editor, Merlin AI, and all other features, you'll need to subscribe to a paid plan. Any code you previously wrote is preserved and will be accessible again once you resubscribe.`,
  },
  {
    question: `Do you offer refunds?`,
    answer: `Yes, we offer a 14-day refund policy. If you're not satisfied with your subscription, please request a refund within 14 days of your purchase date.`,
  },
  {
    question: `Can I cancel my subscription anytime?`,
    answer: `Absolutely! You can manage and cancel your subscription anytime from the Billing Page.`,
  },
];

interface IFAQProps {
  isLegacy: boolean;
}

export const FAQ = ({ isLegacy }: IFAQProps) => {
  const filteredFAQs = QUESTIONS_AND_ANSWERS.filter(
    (faq) => !faq.legacyOnly || isLegacy
  );

  return (
    <VStack pt={40} px={4}>
      <Text fontSize="xxxxLarge" fontWeight="semibold" textAlign="center">
        Frequently asked questions
      </Text>
      <Accordion allowMultiple>
        {filteredFAQs.map((faq) => {
          const answer =
            isLegacy && faq.legacyAnswer ? faq.legacyAnswer : faq.answer;

          return (
            <AccordionItem
              mt={6}
              p={4}
              key={faq.question}
              borderRadius="8px"
              border="1px solid"
              borderColor="whiteAlpha.200"
              background="linear-gradient(136deg, rgba(214, 188, 250, 0.16) 0%, rgba(214, 188, 250, 0.00) 100%)"
              boxShadow="0px 10px 20px 0px rgba(0, 0, 0, 0.15)"
              maxWidth="900px"
            >
              {({ isExpanded }) => (
                <>
                  <h2>
                    <AccordionButton>
                      <Box as="span" flex="1" textAlign="left">
                        <HStack spacing={2}>
                          <Text fontSize="xLarge">{faq.question}</Text>
                        </HStack>
                      </Box>
                      <Icon type={isExpanded ? 'Minus' : 'Add'} size={20} />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel py={4}>
                    <Text fontSize="large" fontWeight="regular">
                      {answer}
                    </Text>
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          );
        })}
      </Accordion>
    </VStack>
  );
};
